/* Prepared Storefront integration: reads automatic discounts without touching the online-store cart. */
((scope) => {
  'use strict';
  const query = `mutation OfferPrice($input: CartInput!, $country: CountryCode!) @inContext(country: $country) {
    cartCreate(input: $input) {
      cart {
        cost { subtotalAmount { amount currencyCode } }
        discountAllocations { discountedAmount { amount currencyCode } }
        lines(first: 100) { nodes { quantity merchandise { ... on ProductVariant { id } } } }
      }
      userErrors { field message }
    }
  }`;

  function minorUnits(value) {
    const match = String(value).match(/^(\d+)(?:\.(\d+))?$/);
    if (!match || /[1-9]/.test((match[2] || '').slice(2))) throw new Error('Invalid Shopify amount');
    const result = BigInt(match[1]) * 100n + BigInt((match[2] || '').slice(0, 2).padEnd(2, '0'));
    if (result > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error('Shopify amount exceeds safe range');
    return Number(result);
  }

  function lineCounts(lines) {
    const counts = new Map();
    for (const line of lines) {
      const id = String(line.id).split('/').pop();
      if (!/^\d+$/.test(id) || !Number.isInteger(line.quantity) || line.quantity < 1) throw new Error('Invalid merchandise line');
      counts.set(id, (counts.get(id) || 0) + line.quantity);
    }
    return counts;
  }

  function readQuote(payload, expectedLines, expectedCurrency) {
    if (payload.errors?.length) throw new Error('Storefront query rejected');
    const result = payload.data?.cartCreate;
    if (!result?.cart || result.userErrors?.length) throw new Error('Shopify could not price this selection');
    const cart = result.cart;
    const actual = lineCounts(cart.lines.nodes.map(line => ({id:line.merchandise?.id, quantity:line.quantity})));
    const expected = lineCounts(expectedLines);
    if (actual.size !== expected.size || [...expected].some(([id, qty]) => actual.get(id) !== qty)) throw new Error('Shopify changed the requested selection');
    const subtotal = cart.cost.subtotalAmount;
    if (subtotal.currencyCode !== expectedCurrency) throw new Error('Shopify market currency does not match the page');
    // subtotalAmount already includes line discounts; cart allocations contain the cart-level discounts.
    let total = minorUnits(subtotal.amount);
    for (const allocation of cart.discountAllocations) {
      if (allocation.discountedAmount.currencyCode !== expectedCurrency) throw new Error('Discount currency mismatch');
      total -= minorUnits(allocation.discountedAmount.amount);
    }
    if (total < 0) throw new Error('Invalid discounted total');
    return {amount:total, currency:expectedCurrency};
  }

  class ShopifyOfferQuote {
    constructor({endpoint, publicToken, country, currency, fetchImpl=(...args)=>scope.fetch(...args), now=Date.now}) {
      const url = new URL(endpoint);
      if (url.protocol !== 'https:') throw new Error('Storefront requires HTTPS');
      if (/^(shpat_|shpua_|shpss_|shptka_)/.test(publicToken || '')) throw new Error('Use a public Storefront token, never an administrative or private token');
      Object.assign(this, {endpoint:url.href, publicToken, country, currency, fetchImpl, now});
      this.cache = new Map();
      this.pending = new Map();
    }

    async price(lines, {force=false}={}) {
      const counts = [...lineCounts(lines)].sort(([a], [b]) => a.localeCompare(b));
      const key = JSON.stringify([this.country, this.currency, counts]);
      const cached = this.cache.get(key);
      if (!force && cached && this.now() - cached.at < 30000) return cached.value;
      if (this.pending.has(key)) return this.pending.get(key);
      const promise = (async () => {
        const headers = {'Content-Type':'application/json'};
        if (this.publicToken) headers['X-Shopify-Storefront-Access-Token'] = this.publicToken;
        const response = await this.fetchImpl(this.endpoint, {
          method:'POST', credentials:'omit', cache:'no-store', headers,
          body:JSON.stringify({query, variables:{country:this.country, input:{buyerIdentity:{countryCode:this.country}, lines:counts.map(([id, quantity]) => ({merchandiseId:'gid://shopify/ProductVariant/' + id, quantity}))}}}),
        });
        if (!response.ok) throw new Error('Storefront pricing unavailable (HTTP ' + response.status + ')');
        const value = readQuote(await response.json(), lines, this.currency);
        this.cache.set(key, {at:this.now(), value});
        return value;
      })();
      this.pending.set(key, promise);
      try {return await promise;} finally {this.pending.delete(key);}
    }
  }

  if (typeof module === 'object' && module.exports) module.exports={ShopifyOfferQuote, readQuote, minorUnits};
  else scope.ShopifyOfferQuote=ShopifyOfferQuote;
})(typeof window === 'undefined' ? globalThis : window);
